import React from 'react';
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  MetricExtended,
  getUnconfiguredMetrics,
  metric_label,
  getMetrics
} from '../../../../lib/metric_capture/metric';
import { resolveObjectFromString } from '../../../../util/validation';
import { MetricRecord } from '@esg/esg-global-types';
import { QuantitativeMetricRecordGridRow } from '../../../metric_collection/quantitative/QuantitativeMetricRecordGrid';

interface MetricSelectProps {
  handleChangeMetrics?: (metrics: Array<MetricExtended> | MetricExtended | null) => void;
  options?: Array<MetricExtended>;
  selected_options?: Array<MetricExtended> | MetricExtended | null;
  metric_records?: Array<MetricRecord | QuantitativeMetricRecordGridRow>;
  disabled?: boolean;
  input_label?: string;
  group_by?: 'metric_group.name' | 'standard.name' | 'standard.version' | 'standard.category';
  multi_select?: boolean;
  tag_limit?: number;
  clearable?: boolean;
  group_id?: string;
  company_id?: string;
  unconfigured_only?: boolean;
  standard_ids?: Array<string>;
  master_list?: boolean;
  loading?: boolean;
  variant?: 'standard' | 'outlined';
  background_color?: string;
  show_icons?: boolean;
  disable_selected_tags?: boolean;
  select_all?: boolean;
  quantitative_only?: boolean;
  primary_metrics_only?: boolean;
}

// Checkbox icons
const check_icon_blank: JSX.Element = <CheckBoxOutlineBlankIcon fontSize="small" />;
const check_icon_filled: JSX.Element = <CheckBoxIcon fontSize="small" />;

/**
 * Metric selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array<MetricExtended> | MetricExtended | null} props.handleChangeMetrics Callback function to notify the parent component when the selected options have changed.
 * @param {Array<MetricExtended>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array<MetricExtended> | MetricExtended | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {Array<MetricRecord | QuantitativeMetricRecordGridRow>} props.metric_records Optionally display Metric Records captured per Metric.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {string} props.group_by Group the options by any one of a variety of grouping options. Defaults to ungrouped.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {string} props.group_id Query configured metrics for the provided group. Works in conjunction with props.company_id. Optionally works in conjunction with unconfigured_only or standard_ids. Defaults to empty list.
 * @param {string} props.company_id Query configured metrics for the provided company. Works in conjunction with props.group_id. Optionally works in conjunction with unconfigured_only or standard_ids. Defaults to empty list.
 * @param {boolean} props.unconfigured_only Query only unconfigured metrics for the provided group_id and company_id. Optionally works in conjunction with standard_ids. Defaults to false.
 * @param {Array<string>} props.standard_ids Query metrics for a custom list of standard id's. Not specifying group_id or company_id will query the master list metrics for specified standards.
 * @param {boolean} props.master_list Forcefully query metrics from the master list. Does not require group_id and company_id. Defaults to false.
 * @param {boolean} props.loading A flag to set the autocomplete into a loading state for things such as fetching entries from database.
 * @param {string} props.variant An optional styling parameter for the textbox input of the autocomplete.
 * @param {string} props.background_color An optional styling parameter for the background color of the input component.
 * @param {boolean} props.show_icon Optional parameter to control the showing of metric record and accumulation method icons.
 * @param {boolean} props.disable_selected_tags Optionally replace the selected tags with text when autocomplete is expanded to render input at a predictable height.
 * @param {boolean} props.select_all Optionally add a select all checkbox to the input component of the autocomplete when multiple selection is allowed.
 * @param {boolean} props.quantitative_only Optionally only include metric options which are quantitative.
 * @param {boolean} props.primary_metrics_only Optionally only include metric options which are not calculated (primary).
 * @returns {JSX.Element}
 */
const MetricSelect = (props: MetricSelectProps) => {
  const [optionList, setOptions] = React.useState<Array<MetricExtended>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<
    Array<MetricExtended> | MetricExtended | null
  >(props.multi_select ? [] : null);

  // Query validations
  const get_unconfigured_metrics: boolean =
    props.options === undefined &&
    !props.master_list &&
    props.group_id &&
    props.company_id &&
    props.unconfigured_only &&
    !props.standard_ids
      ? true
      : false;
  const get_configured_metrics: boolean =
    props.options === undefined &&
    !props.master_list &&
    props.group_id &&
    props.company_id &&
    !props.unconfigured_only &&
    !props.standard_ids
      ? true
      : false;

  const get_configured_metrics_for_standards: boolean =
    props.options === undefined &&
    !props.master_list &&
    props.group_id &&
    props.company_id &&
    !props.unconfigured_only &&
    props.standard_ids
      ? true
      : false;

  const get_master_list_metrics_for_standards: boolean =
    props.options === undefined &&
    !props.master_list &&
    !props.group_id &&
    !props.company_id &&
    props.standard_ids
      ? true
      : false;

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<MetricExtended> = props.options
        ? // Metrics supplied by the parent component.
          props.options
        : // All master list metrics.
          props.master_list
          ? await getMetrics(true, undefined, undefined, props.standard_ids, true)
          : get_unconfigured_metrics
            ? // Unconfigured metrics per company.
              await getUnconfiguredMetrics(
                props.group_id || '',
                props.company_id || '',
                props.standard_ids,
                true
              )
            : get_configured_metrics
              ? // Configured metrics per company.
                await getMetrics(false, props.group_id, props.company_id, props.standard_ids, true)
              : get_master_list_metrics_for_standards
                ? // Metrics for specific standards from the master list.
                  await getMetrics(true, undefined, undefined, props.standard_ids, true)
                : // Configured metrics for specific standards per company.
                  get_configured_metrics_for_standards
                  ? await getMetrics(
                      false,
                      props.group_id,
                      props.company_id,
                      props.standard_ids,
                      true
                    )
                  : [];
      const filtered_options: Array<MetricExtended> = options.filter(
        (metric: MetricExtended) =>
          !props.quantitative_only ||
          ('is_quantitative' in metric.standard &&
            metric.standard.is_quantitative &&
            (!props.primary_metrics_only || metric.is_calculated === false))
      );
      if (filtered_options) setOptions(filtered_options);
    })();
  }, [props.group_id, props.company_id, props.options]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  // Group selection
  const selected_options_array: Array<MetricExtended> =
    selectedOptions && !Array.isArray(selectedOptions)
      ? [selectedOptions]
      : selectedOptions && Array.isArray(selectedOptions)
        ? selectedOptions
        : [];

  const checkGroup = (group_key: string): boolean => {
    const group_length: number = optionList.filter(
      (option: MetricExtended) =>
        (props.group_by ? resolveObjectFromString(props.group_by, option) : '') === group_key
    ).length;
    const selected_group_length: number = selected_options_array.filter(
      (option: MetricExtended) =>
        (props.group_by ? resolveObjectFromString(props.group_by, option) : '') === group_key
    ).length;
    return group_length === selected_group_length;
  };

  const checkAll = (): boolean => {
    if (Array.isArray(selectedOptions)) {
      return optionList.length === selectedOptions.length;
    }
    return false;
  };

  const changeGroup = (group_key: string): void => {
    const grouped_options: Array<MetricExtended> = optionList.filter(
      (option: MetricExtended) =>
        (props.group_by ? resolveObjectFromString(props.group_by, option) : '') === group_key
    );
    const selected_group_options: Array<MetricExtended> = selected_options_array.filter(
      (option: MetricExtended) =>
        (props.group_by ? resolveObjectFromString(props.group_by, option) : '') === group_key
    );
    if (selected_group_options.length > 0) {
      const selected_without_group: Array<MetricExtended> = (
        Array.isArray(selectedOptions) ? selectedOptions : []
      ).filter(
        (option: MetricExtended) =>
          (props.group_by ? resolveObjectFromString(props.group_by, option) : '') !== group_key
      );
      setSelectedOptions(selected_without_group);
      props.handleChangeMetrics && props.handleChangeMetrics(selected_without_group);
    } else {
      const selected_with_group: Array<MetricExtended> = [
        ...(Array.isArray(selectedOptions) ? selectedOptions : []),
        ...grouped_options
      ];
      setSelectedOptions(selected_with_group);
      props.handleChangeMetrics && props.handleChangeMetrics(selected_with_group);
    }
  };

  const changeAll = (): void => {
    if (Array.isArray(selectedOptions)) {
      if (selectedOptions.length > 0) {
        setSelectedOptions([]);
        props.handleChangeMetrics && props.handleChangeMetrics([]);
      } else {
        setSelectedOptions(optionList);
        props.handleChangeMetrics && props.handleChangeMetrics(optionList);
      }
    }
  };

  return (
    <Autocomplete
      id="metric-select"
      fullWidth
      disabled={props.disabled}
      loading={props.loading}
      limitTags={props.tag_limit}
      disableClearable={props.clearable === false}
      options={optionList
        .sort((a: MetricExtended, b: MetricExtended) => (a.name < b.name ? 1 : -1))
        .sort((a: MetricExtended, b: MetricExtended) =>
          a.metric_group.name > b.metric_group.name ? 1 : -1
        )}
      sx={{ backgroundColor: props.background_color ?? 'transparent' }}
      groupBy={
        props.group_by
          ? (option: MetricExtended) =>
              props.group_by ? resolveObjectFromString(props.group_by, option) : ''
          : undefined
      }
      value={selectedOptions}
      getOptionLabel={(option: MetricExtended) => {
        return option.name;
      }}
      disableCloseOnSelect={props.multi_select}
      isOptionEqualToValue={(option: MetricExtended, value: MetricExtended) =>
        option.id === value.id
      }
      multiple={props.multi_select}
      noOptionsText={`No ${metric_label.many.toLowerCase()}`}
      renderValue={
        !props.multi_select || !props.disable_selected_tags
          ? undefined
          : (value: MetricExtended | Array<MetricExtended>) => {
              return Array.isArray(value) && value.length > 0
                ? `${value.length} ${value.length === 1 ? metric_label.one : metric_label.many}`
                : undefined;
            }
      }
      renderOption={(render_props, option: MetricExtended, { selected }) => {
        return (
          <Tooltip title={option.description} arrow placement="right-end" key={option.id}>
            <Box
              component="li"
              sx={{ display: 'flex', alignItems: 'center' }}
              {...render_props}
              key={option.id}
            >
              {props.multi_select && (
                <Checkbox
                  icon={check_icon_blank}
                  checkedIcon={check_icon_filled}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              <Typography component="span">{option.name}</Typography>
              {props.show_icons && option.accumulation_method === 'change_of_state' && (
                <Tooltip title="Change of State Metric" arrow placement="bottom">
                  <SwapVertIcon sx={{ ml: 1 }} color="primary" />
                </Tooltip>
              )}
            </Box>
          </Tooltip>
        );
      }}
      renderGroup={
        props.multi_select
          ? (params: AutocompleteRenderGroupParams) => (
              <Box component="div" key={params.key}>
                <Box
                  component="li"
                  style={{
                    fontSize: '0.85rem',
                    color: '#616161',
                    fontWeight: 500
                  }}
                >
                  <Checkbox
                    icon={check_icon_blank}
                    checkedIcon={check_icon_filled}
                    checked={checkGroup(params.group)}
                    onChange={() => {
                      changeGroup(params.group);
                    }}
                  />
                  {params.group}
                </Box>
                <span>{params.children}</span>
              </Box>
            )
          : (params: AutocompleteRenderGroupParams) => (
              <Box component="div" key={params.key}>
                <Box
                  component="li"
                  style={{
                    fontSize: '0.85rem',
                    color: '#616161',
                    fontWeight: 500,
                    paddingLeft: 15
                  }}
                >
                  <Typography>{params.group}</Typography>
                </Box>

                <Typography
                  style={{
                    paddingLeft: 25
                  }}
                >
                  {params.children}
                </Typography>
              </Box>
            )
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={props.input_label ?? metric_label.one}
          variant={props.variant ?? 'standard'}
          slotProps={{
            htmlInput: { ...params.inputProps },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {props.select_all && props.multi_select && (
                    <Box component="div">
                      <Box
                        component="li"
                        style={{
                          fontSize: '0.85rem',
                          color: '#616161',
                          fontWeight: 500
                        }}
                      >
                        <Checkbox
                          icon={check_icon_blank}
                          checkedIcon={check_icon_filled}
                          checked={checkAll()}
                          onChange={() => {
                            changeAll();
                          }}
                        />
                        Select All
                      </Box>
                    </Box>
                  )}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: Array<MetricExtended> | MetricExtended | null
      ) => {
        setSelectedOptions(value);
        props.handleChangeMetrics && props.handleChangeMetrics(value);
      }}
    />
  );
};

export default MetricSelect;
